import { ReactNode, useEffect, useState } from 'react'
import { 
  useSelector,
  useDispatch,
} from 'react-redux'
import {
  useLocation,
  useHistory
} from 'react-router'
import {
  identityEmailVerificationUrl,
  identityVerificationUrl,
  loginUrl,
  otpUrl,
} from '../../../containers/common/constant/appUrl'
import {
  selectIsLoggedIn,
  selectIsOTPAuthenticated,
  selectIsVerifiedEmail,
  selectIsVerifiedTel,
  detectLoginStatus,
} from '../../../containers/common/store/slices/authority'
import { showLoading } from '../../../containers/common/store/slices/application'
import { selectSystemControl } from '../../../containers/common/store/slices/systemControl'
import { yesNo } from '../../../containers/common/constant/classification'
import { executeGetAccountOtp } from '../../../dataAccess/webApi/dao/accountDao'

interface AuthenticatedGuardProps {
  children: ReactNode
  /**
   * trueの場合、電話番号の検証フラグチェックは行わない
   * 本人確認用の電話番号検証画面の為に用意したフラグ
   * ※検証画面はログインしている必要がある為
   */
  isIgnoreVerifiedTel?: boolean

  isIgnoreVerifiedEmail?: boolean
}

/**
 * ログイン状態をチェックし、
 * ログイン状態なら子コントロール(各ページ)を実行
 * 未ログイン状態ならログイン画面へリダイレクトする
 * ログイン必須画面が未ログインで実行されないようにする部品。
 *
 * 本人確認が未実施の場合、検証コードを確認する画面へ遷移する。
 *
 * ログイン後、本来の遷移先へ遷移出来るようにするために
 * リダイレクト時に現在のロケーション情報をhistoryのstateとして設定している。
 */
export const AuthenticatedGuard = (props: AuthenticatedGuardProps) => {
  const isLoggedIn = useSelector(selectIsLoggedIn)
  const isVerifiedTel = useSelector(selectIsVerifiedTel)
  const isVerifiedEmail = useSelector(selectIsVerifiedEmail)
  const { smsSubscriptionFlag, phoneCertificationFlag } = useSelector(selectSystemControl)
  const isOTPAuthenticated = useSelector(selectIsOTPAuthenticated)
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()

  // リダイレクトパス
  const [redirectPathName, setRedirectPathName] = useState('')
  
  const initialize = async () => {
    
    // いずれかの認証が未済の場合のリダイレクトパス
    let newPath = null
    
    if (isLoggedIn) {

      let emailVerifiedFlag
      let telVerifiedFlag

      let isFetched = false

      if (!isVerifiedEmail || !isVerifiedTel) {
        // バックエンドからメール/SMS認証状態を取得
        const authLog = await executeGetAccountOtp();
        ({
          emailVerifiedFlag,
          telVerifiedFlag,
        } = authLog.result);

        isFetched = true
      }

      if (!isFetched) {
        // バックエンドからフラグを取得していない場合、ストアを参照
        emailVerifiedFlag = isVerifiedEmail ? yesNo.yes : yesNo.no
        telVerifiedFlag = isVerifiedTel ? yesNo.yes : yesNo.no
      } else {
        // バックエンドからフラグを取得した場合、ストアと一致しているか検証し、一致していなければストア更新
        const emailMismatch = (emailVerifiedFlag === yesNo.yes) !== isVerifiedEmail
        const telMismatch = (telVerifiedFlag === yesNo.yes) !== isVerifiedTel

        if (emailMismatch || telMismatch) {
          dispatch(showLoading(async () => await dispatch(detectLoginStatus())))
        }
      }
  
      if (!props.isIgnoreVerifiedEmail && emailVerifiedFlag === yesNo.no) {
        // メール認証未済
        newPath = identityEmailVerificationUrl.url()
      } else if (!props.isIgnoreVerifiedTel && telVerifiedFlag === yesNo.no && smsSubscriptionFlag === yesNo.yes && phoneCertificationFlag === yesNo.yes) {
        // 電話番号認証未済
        newPath = identityVerificationUrl.url()
      } else if (!isOTPAuthenticated && emailVerifiedFlag === yesNo.yes) {
        // 2要素認証未済
        if (telVerifiedFlag === yesNo.yes && smsSubscriptionFlag === yesNo.yes && phoneCertificationFlag === yesNo.yes) {
          newPath = otpUrl.url()
        }
        if (smsSubscriptionFlag === yesNo.no || phoneCertificationFlag === yesNo.no) {
          newPath = otpUrl.url()
        }
      }
    } else {
      // ログイン未済
      newPath = loginUrl.url()
    }

    if (newPath && location.pathname !== newPath && redirectPathName !== newPath) {
      // いずれかの認証が未済の場合、リダイレクトパスをセット
      setRedirectPathName(newPath)
    } else {
      // 認証済みのため、リダイレクトパスを初期化
      setRedirectPathName('')
    }
  }

  /**
   * リダイレクトパスをセットする
   */
  useEffect(() => {
    initialize()
  }, [
    isLoggedIn, isVerifiedEmail, isVerifiedTel,
    isOTPAuthenticated, location.pathname,
  ])
  
  /**
   * リダイレクトを行う
  */
  useEffect(() => {
    if (redirectPathName && location.pathname !== redirectPathName) {
      // リダイレクトパスがセットされている場合、リダイレクトを行う
      history.replace({
        pathname: redirectPathName,
        state: { from: location },
      })
    }
  }, [redirectPathName, location.pathname])

  // リダイレクトパスがセットされている場合、アクセス先には遷移させない
  if (!isLoggedIn) {
    // ログイン未済
    return null
  }

  if (!props.isIgnoreVerifiedEmail && !isVerifiedEmail) {
    // メール認証未済
    return null
  }
  if (!props.isIgnoreVerifiedTel && !isVerifiedTel && smsSubscriptionFlag === yesNo.yes && phoneCertificationFlag === yesNo.yes) {
    // 電話番号認証未済
    return null
  }
  if (!isOTPAuthenticated && isVerifiedEmail) {
    // 2要素認証未済
    if (isVerifiedTel && smsSubscriptionFlag === yesNo.yes && phoneCertificationFlag === yesNo.yes) {
      return null
    }
    if (smsSubscriptionFlag === yesNo.no || phoneCertificationFlag === yesNo.no) {
      return null
    }
  }

  return <>{props.children}</>
}
